import { AxiosError } from "axios";
import { HttpRequestGet, HttpRequestPost, HttpRequestPut } from "../../service";

export interface MerchantOrganisationDetailsAddress {
  Line1?: string;
  Line2?: string;
  Line3?: string;
  PostalCode?: string;
  County?: string;
  City?: string;
  Lat?: number;
  Long?: number;
}

export interface MerchantOrganisationUserDetails {
  Id: string;
  Email: string;
  FirstName?: string;
  LastName?: string;
  PhoneNumber?: string;
  ShouldSendEmailNotification?: boolean;
  ShouldSendNotification?: boolean;
}

export interface MerchantOrganisationShopSummary {
  Id: string;
  Name: string;
  Email: string;
  Archived?: string;
}

export interface MerchantOrganisationDetailsResponseModel {
  Id: string;
  PhoneNumber?: string;
  InfoEmail?: string;
  Name: string;
  LogoUrl?: string;
  BrandColour: string;
  Address: MerchantOrganisationDetailsAddress;
  VatNumber?: string;
  InvoiceName: string;
  StripeAccountId?: string;
  Users: MerchantOrganisationUserDetails[];
  ShopSummaries: MerchantOrganisationShopSummary[];
}

export interface EditMerchantOrganisationRequestModel {
  PhoneNumber?: string;
  InfoEmail?: string;
  LogoUrl?: string;
  BrandColour?: string;
  Address: {
    Line1?: string;
    Line2?: string;
    Line3?: string;
    PostalCode?: string;
    County?: string;
    City?: string;
    Lat?: number;
    Long?: number;
  };
  VatNumber?: string;
  InvoiceName?: string;
  StripeAccountId?: string;
}

export interface GetMerchantShopsRequestModel {
  Page: number;
  Size: number;
  Query: string;
}

export interface MerchantShopListResponseModelShop {
  Id: string;
  Name: string;
  Line1?: string;
  Line2?: string;
  City: string;
  County: string;
  PostalCode: string;
  ImageUrl?: string;
  Archived: boolean;
}

export interface MerchantShopListResponseModel {
  Shops: MerchantShopListResponseModelShop[];
  Count: number;
}

export function getMerchantOrganisationDetails(
  merchantId: string,
  success: (response: MerchantOrganisationDetailsResponseModel) => void,
  failure: (error: AxiosError<any>) => void
): void {
  HttpRequestGet(
    `/admin/merchant/${merchantId}`,
    () => undefined,
    (response: { Result: MerchantOrganisationDetailsResponseModel } | MerchantOrganisationDetailsResponseModel) => {
      // Handle both wrapped and unwrapped responses
      const data = (response as any).Result || response;
      success(data);
    },
    (error: any) => failure(error),
    true
  );
}

export function editMerchantOrganisation(
  merchantId: string,
  data: EditMerchantOrganisationRequestModel,
  success: (response: any) => void,
  failure: (error: AxiosError<any>) => void
): void {
  HttpRequestPut(
    `/admin/merchant/${merchantId}`,
    () => undefined,
    (response: any) => success(response),
    (error: any) => failure(error),
    true,
    data
  );
}

export function getMerchantShops(
  merchantId: string,
  body: GetMerchantShopsRequestModel,
  success: (response: MerchantShopListResponseModel) => void,
  failure: (error: AxiosError<any>) => void
): void {
  HttpRequestPost(
    `/admin/merchant/${merchantId}/shop/list`,
    () => undefined,
    (response: { Result: MerchantShopListResponseModel }) => {
      // Handle both wrapped and unwrapped responses
      const data = (response as any).Result || response;
      success(data);
    },
    (error: any) => failure(error),
    true,
    body
  );
}

export interface EditMerchantUserData {
  Email: string;
  FirstName?: string;
  LastName?: string;
  PhoneNumber?: string;
  ShouldSendEmailNotification?: boolean;
  ShouldSendNotification?: boolean;
}

export interface InviteMerchantUserData {
  FirstName: string;
  LastName: string;
  Email: string;
  PhoneNumber?: string;
  ShouldSendEmailNotification?: boolean;
  ShouldSendNotification?: boolean;
}

export function editMerchantUser(
  merchantId: string,
  userId: string,
  data: EditMerchantUserData,
  success: (response: any) => void,
  failure: (error: AxiosError<any>) => void
): void {
  HttpRequestPut(
    `/admin/merchant/${merchantId}/user/${userId}/edit`,
    () => undefined,
    (response: any) => success(response),
    (error: any) => failure(error),
    true,
    data
  );
}

export interface InviteMerchantResponseModel {
  Password: string;
}

export function inviteMerchantUser(
  merchantId: string,
  data: InviteMerchantUserData,
  success: (response: InviteMerchantResponseModel) => void,
  failure: (error: AxiosError<any>) => void
): void {
  HttpRequestPost(
    `/admin/merchant/${merchantId}/user/invite`,
    () => undefined,
    (response: { Result: InviteMerchantResponseModel }) => {
      const data = (response as any).Result || response;
      success(data);
    },
    (error: any) => failure(error),
    true,
    data
  );
}
